import { useCallback, useEffect, useState } from "react";
import { CreditCard, Eye, EyeOff, Plug, Plus, Star, Trash2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";

const inputCls = "h-10 px-3 w-full bg-[#0F1115] border border-[#272B33] rounded-lg text-sm text-neutral-200 focus:outline-none focus:border-blue-500";

function SecretInput({ field, value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input type={show ? "text" : "password"} value={value} placeholder={placeholder}
        onChange={(e) => onChange(field.key, e.target.value)} className={`${inputCls} pr-10`}
        data-testid={`pp-field-${field.key}`} autoComplete="new-password" />
      <button type="button" onClick={() => setShow(!show)} tabIndex={-1}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300">
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

function ProviderForm({ schemas, initial, onSave, onCancel }) {
  const isEdit = !!initial?.provider_id;
  const [code, setCode] = useState(initial?.code || "garanti");
  const [name, setName] = useState(initial?.name || "");
  const [mode, setMode] = useState(initial?.mode || "TEST");
  const [active, setActive] = useState(initial?.active ?? false);
  const [threeds, setThreeds] = useState(initial?.threeds_enabled ?? true);
  const [installments, setInstallments] = useState((initial?.installment_counts || []).join(","));
  const [creds, setCreds] = useState({});
  const [saving, setSaving] = useState(false);

  const schema = schemas.find((s) => s.code === code);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const installment_counts = [...new Set(installments.split(",").map((t) => parseInt(t.trim(), 10)).filter((n) => n >= 1 && n <= 24))].sort((a, b) => a - b);
      const payload = { code, name: name || schema?.label, mode, active, threeds_enabled: threeds, credentials: creds, installment_counts };
      if (isEdit) await api.put(`/admin/payment-providers/${initial.provider_id}`, payload);
      else await api.post("/admin/payment-providers", payload);
      toast.success(isEdit ? "Sağlayıcı güncellendi" : "Sağlayıcı eklendi");
      onSave();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-[#181B20] border border-blue-500/30 rounded-2xl p-5 space-y-4" data-testid="payment-provider-form">
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[11px] text-neutral-500 block mb-1">Sağlayıcı</label>
          {isEdit ? (
            <p className="text-white text-sm font-semibold py-2">{schema?.label || code}</p>
          ) : (
            <select value={code} onChange={(e) => { setCode(e.target.value); setCreds({}); }} className={inputCls} data-testid="pp-code-select">
              {schemas.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
            </select>
          )}
        </div>
        <div>
          <label className="text-[11px] text-neutral-500 block mb-1">Görünen Ad</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={schema?.label} className={inputCls} data-testid="pp-name" />
        </div>
        <div>
          <label className="text-[11px] text-neutral-500 block mb-1">Mod</label>
          <select value={mode} onChange={(e) => setMode(e.target.value)} className={inputCls} data-testid="pp-mode">
            <option value="TEST">Test</option>
            <option value="LIVE">Canlı (LIVE)</option>
          </select>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {(schema?.fields || []).map((f) => {
          const existing = initial?.credentials?.[f.key];
          return (
            <div key={f.key}>
              <label className="text-[11px] text-neutral-500 block mb-1">
                {f.label} {f.required === false && <span className="text-neutral-600">(opsiyonel)</span>}
              </label>
              {f.secret ? (
                <SecretInput field={f} value={creds[f.key] || ""}
                  onChange={(k, v) => setCreds({ ...creds, [k]: v })}
                  placeholder={existing ? `Mevcut: ${existing} (boş = değişmez)` : "••••••••"} />
              ) : (
                <input value={creds[f.key] ?? (isEdit ? existing || "" : "")}
                  onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
                  className={inputCls} data-testid={`pp-field-${f.key}`} />
              )}
            </div>
          );
        })}
      </div>

      {code === "garanti" && (
        <div>
          <label className="text-[11px] text-neutral-500 block mb-1">Taksit Seçenekleri (virgülle, örn: 1,2,3,6,9,12)</label>
          <input value={installments} onChange={(e) => setInstallments(e.target.value)} placeholder="1,2,3,6,9,12"
            className={inputCls} data-testid="pp-installments" />
          <p className="text-[10px] text-neutral-600 mt-1">Boş bırakılırsa yalnızca tek çekim sunulur. Vade farkı varsa bankanın 3D ekranında uygulanır.</p>
        </div>
      )}

      <div className="flex items-center gap-5 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer">
          <input type="checkbox" checked={threeds} onChange={(e) => setThreeds(e.target.checked)} className="accent-blue-500 w-4 h-4" data-testid="pp-3ds" /> 3D Secure aktif
        </label>
        <label className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-blue-500 w-4 h-4" data-testid="pp-active" /> Aktif
        </label>
      </div>

      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="h-10 px-5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 disabled:opacity-50" data-testid="pp-save">
          {saving ? "Kaydediliyor..." : isEdit ? "Güncelle" : "Kaydet"}
        </button>
        <button type="button" onClick={onCancel} className="h-10 px-4 rounded-xl border border-[#272B33] text-sm text-neutral-400" data-testid="pp-cancel">Vazgeç</button>
      </div>
      <p className="text-[11px] text-neutral-600 flex items-center gap-1.5"><ShieldCheck size={12} /> API bilgileri AES-256 ile şifrelenerek saklanır; ekranda ve loglarda asla açık gösterilmez.</p>
    </form>
  );
}

export default function PaymentMethodsPage() {
  const [providers, setProviders] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [form, setForm] = useState(null); // null | {} | provider
  const [testing, setTesting] = useState("");

  const load = useCallback(async () => {
    const [{ data: list }, { data: sch }] = await Promise.all([
      api.get("/admin/payment-providers"), api.get("/admin/payment-providers/schemas")]);
    setProviders(list);
    setSchemas(sch.providers);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const runTest = async (p) => {
    setTesting(p.provider_id);
    try {
      const { data } = await api.post(`/admin/payment-providers/${p.provider_id}/test`, {}, { timeout: 60000 });
      data.ok ? toast.success(data.message) : toast.error(data.message);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setTesting(""); }
  };

  const makeDefault = async (p) => {
    try {
      await api.post(`/admin/payment-providers/${p.provider_id}/default`);
      toast.success(`${p.name} varsayılan ödeme sağlayıcısı oldu`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggle = async (p) => {
    try {
      await api.post(`/admin/payment-providers/${p.provider_id}/toggle`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`'${p.name}' silinsin mi? Geçmiş sipariş kayıtları etkilenmez.`)) return;
    try {
      await api.delete(`/admin/payment-providers/${p.provider_id}`);
      toast.success("Sağlayıcı silindi");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-payment-methods">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-white">Ödeme Yöntemleri</h1>
          <p className="text-xs text-neutral-500 mt-1">Sanal POS sağlayıcılarını panelden yönetin — sağlayıcı değişimi mevcut siparişleri etkilemez.</p>
        </div>
        <button onClick={() => setForm({})} className="h-10 px-4 rounded-xl bg-blue-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-blue-500" data-testid="payment-provider-new">
          <Plus size={15} /> Yeni Ödeme Sağlayıcı
        </button>
      </div>

      {form && <ProviderForm schemas={schemas} initial={form.provider_id ? form : null}
        onSave={() => { setForm(null); load(); }} onCancel={() => setForm(null)} />}

      <div className="grid lg:grid-cols-2 gap-4">
        {providers.map((p) => (
          <div key={p.provider_id} className={`bg-[#181B20] border rounded-2xl p-5 space-y-3 ${p.is_default ? "border-amber-500/50" : "border-[#272B33]"}`}
            data-testid={`payment-provider-card-${p.code}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-white font-bold flex items-center gap-2">
                  <CreditCard size={15} className="text-blue-400" /> {p.name}
                  {p.is_default && <span className="text-[10px] font-bold bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full flex items-center gap-1"><Star size={10} /> VARSAYILAN</span>}
                </p>
                <p className="text-[11px] text-neutral-500 mt-1">{p.schema?.label} · Mod: <span className={`font-mono-ed ${p.mode === "LIVE" ? "text-emerald-400" : "text-amber-400"}`}>{p.mode}</span> · 3D Secure: {p.threeds_enabled ? "Açık" : "Kapalı"}</p>
              </div>
              <button onClick={() => toggle(p)}
                className={`text-[11px] font-bold px-3 py-1.5 rounded-full ${p.active ? "bg-emerald-500/15 text-emerald-400" : "bg-neutral-500/15 text-neutral-400"}`}
                data-testid={`pp-toggle-${p.code}`}>
                {p.active ? "Aktif" : "Pasif"}
              </button>
            </div>

            {/* Maskeli kimlik özeti */}
            <div className="bg-[#0F1115] rounded-xl px-3.5 py-2.5 space-y-1" data-testid={`pp-creds-${p.code}`}>
              {(p.schema?.fields || []).map((f) => {
                const v = p.credentials?.[f.key];
                if (!v) return null;
                return (
                  <p key={f.key} className="text-[11px] flex justify-between gap-3">
                    <span className="text-neutral-500">{f.label}</span>
                    <span className="font-mono-ed text-neutral-300">{v}</span>
                  </p>
                );
              })}
              {!Object.values(p.credentials || {}).some(Boolean) && (
                <p className="text-[11px] text-neutral-600">Kimlik bilgisi girilmedi</p>
              )}
            </div>

            {p.last_test && (
              <p className={`text-[11px] flex items-center gap-1.5 ${p.last_test.ok ? "text-emerald-400" : "text-rose-400"}`} data-testid={`pp-last-test-${p.code}`}>
                {p.last_test.ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                {p.last_test.ok ? "Bağlantı başarılı" : "Bağlantı başarısız"} — {p.last_test.message}
                <span className="text-neutral-600 ml-1">({fmtDate(p.last_test.at)})</span>
              </p>
            )}

            <div className="flex gap-2 flex-wrap pt-1">
              <button onClick={() => runTest(p)} disabled={testing === p.provider_id}
                className="h-9 px-3.5 rounded-xl border border-[#272B33] text-xs text-neutral-300 hover:border-blue-500 flex items-center gap-1.5 disabled:opacity-50"
                data-testid={`pp-test-${p.code}`}>
                <Plug size={13} className={testing === p.provider_id ? "animate-pulse" : ""} /> {testing === p.provider_id ? "Test ediliyor..." : "Bağlantıyı Test Et"}
              </button>
              <button onClick={() => setForm(p)} className="h-9 px-3.5 rounded-xl border border-[#272B33] text-xs text-neutral-300 hover:border-blue-500" data-testid={`pp-edit-${p.code}`}>Düzenle</button>
              {!p.is_default && (
                <button onClick={() => makeDefault(p)} className="h-9 px-3.5 rounded-xl border border-amber-600/40 text-xs text-amber-400 hover:border-amber-500" data-testid={`pp-default-${p.code}`}>Varsayılan Yap</button>
              )}
              {!p.is_default && (
                <button onClick={() => remove(p)} className="h-9 px-3 rounded-xl border border-rose-600/30 text-xs text-rose-400 hover:border-rose-500" data-testid={`pp-delete-${p.code}`}><Trash2 size={13} /></button>
              )}
            </div>
          </div>
        ))}
        {providers.length === 0 && <p className="text-neutral-500 text-sm py-10 text-center lg:col-span-2">Henüz ödeme sağlayıcısı yok</p>}
      </div>
    </div>
  );
}
